import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function MainPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // ✅ 페이지 진입 시 항상 맨 위로
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [userName, setUserName] = useState('사용자');
  const [university, setUniversity] = useState('대학교');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTodoVisible, setIsTodoVisible] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().getDate());

  // ✅ 날씨 state 추가
  const [weatherCondition, setWeatherCondition] = useState(null);

  const [notices, setNotices] = useState([]);
  const [tasksByDate, setTasksByDate] = useState({});
  const [aiSummary, setAiSummary] = useState({ title: '요약 데이터 없음', content: 'AI 강의 학습에서 파일을 업로드해 요약해보세요.' });
  const [dailyQuiz, setDailyQuiz] = useState({ question: "등록된 예상 문제가 없습니다.", answer: "AI 강의 학습 메뉴를 이용해주세요." });
  const [urgentTask, setUrgentTask] = useState({ dDay: '-', title: '임박한 일정이 없습니다', detail: '플래너에서 일정을 추가해보세요.' });
  const [teamProjects, setTeamProjects] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [newJobs, setNewJobs] = useState([]);

  const [urlAnalysisResult, setUrlAnalysisResult] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [interestUrl, setInterestUrl] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyDept, setNewCompanyDept] = useState('');
  const [savedCompanies, setSavedCompanies] = useState([]);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  const aiUrl = process.env.REACT_APP_AI_URL || 'https://campus-life-ai.onrender.com';

  useEffect(() => {
    const userData = localStorage.getItem('currentUser');
    let userId = '2';
    let universityName = '한남대학교';
    if (userData) {
      try {
        const loggedInUser = JSON.parse(userData);
        if (loggedInUser && loggedInUser.name) setUserName(loggedInUser.name);
        if (loggedInUser && loggedInUser.userId) userId = String(loggedInUser.userId);
        if (loggedInUser && loggedInUser.university) {
          setUniversity(loggedInUser.university);
          universityName = loggedInUser.university;
        }
      } catch (e) {}
    }

    // ✅ 날씨 API 호출 (위치 권한 사용)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${process.env.REACT_APP_WEATHER_API_KEY}`)
            .then((res) => res.json())
            .then((data) => {
              const id = data.weather[0].id;
              if (id >= 200 && id < 600) {
                setWeatherCondition('rain');
              } else if (id >= 600 && id < 700) {
                setWeatherCondition('snow');
              } else if (id >= 700 && id < 800) {
                setWeatherCondition('cloudy');
              } else if (id >= 801 && id <= 804) {
                setWeatherCondition('cloudy');
              } else {
                setWeatherCondition(null);
              }
            })
            .catch(() => setWeatherCondition(null));
        },
        () => setWeatherCondition(null)
      );
    }

    const summaryData = localStorage.getItem('summary');
    if (summaryData) {
      const lines = summaryData.split('\n').filter(l => l.trim() !== '');
      const summaryLines = lines.filter(l => !l.match(/^(\d+[.)]\s|[①②③④⑤]|정답\s*[:：]|예상 시험 문제)/));
      setAiSummary({
        title: summaryLines[0] || '강의 요약',
        content: summaryLines.slice(1, 3).join(' ') || '상세 내용이 없습니다.'
      });
      const examIdx = lines.findIndex(l =>
        l.includes('예상 시험 문제') || l.includes('예상문제') || l.includes('시험 문제')
      );
      const questionLines = examIdx >= 0 ? lines.slice(examIdx + 1) : [];
      const questionLineRe = /^(?:문제\s*|Q\s*)?(\d+)[.)]\s*(.+)/i;
      const optionLineRe   = /^[①②③④⑤]|^[a-eA-E][).]\s/;
      const answerLineRe   = /^정답\s*[:：]\s*([①②③④⑤\d])/;
      const circledToIdx   = { '①':0,'②':1,'③':2,'④':3,'⑤':4 };
      const parsedQuizzes = [];
      let i = 0;
      while (i < questionLines.length) {
        const qMatch = questionLines[i].match(questionLineRe);
        if (qMatch) {
          const questionText = qMatch[2];
          const options = [];
          let answerIdx = null;
          i++;
          while (i < questionLines.length && optionLineRe.test(questionLines[i])) {
            options.push(questionLines[i].replace(/^[①②③④⑤]\s*/,'').replace(/^[a-eA-E][).]\s*/,'').trim());
            i++;
          }
          if (i < questionLines.length && answerLineRe.test(questionLines[i])) {
            const ansChar = questionLines[i].match(answerLineRe)[1];
            answerIdx = circledToIdx[ansChar] !== undefined ? circledToIdx[ansChar] : parseInt(ansChar) - 1;
            i++;
          }
          parsedQuizzes.push({ question: questionText, options, answerIdx });
        } else { i++; }
      }
      if (parsedQuizzes.length > 0) {
        const pick = parsedQuizzes[Math.floor(Math.random() * parsedQuizzes.length)];
        let answerText = '작성하신 요약본을 복습해보세요!';
        if (pick.answerIdx !== null && pick.options.length > pick.answerIdx) {
          answerText = `${['①','②','③','④','⑤'][pick.answerIdx]} ${pick.options[pick.answerIdx]}`;
        } else if (pick.options.length === 0) {
          const summaryKeywords = summaryLines
            .filter(l => l.startsWith('-') || l.startsWith('•'))
            .map(l => l.replace(/^[-•]\s*/, '').replace(/\*\*/g, '').trim())
            .slice(0, 2).join(' / ');
          answerText = summaryKeywords ? `핵심 키워드: ${summaryKeywords}` : '강의 요약의 핵심 개념을 중심으로 서술해보세요!';
        }
        setDailyQuiz({ question: pick.question, answer: answerText });
      } else {
        setDailyQuiz({ question: '오늘 강의를 요약해보세요!', answer: 'AI 강의 학습 메뉴를 이용해주세요.' });
      }
    }

    fetch(`${apiUrl}/api/schedules/${userId}`)
      .then(res => res.json())
      .then(data => {
        const parsedTasks = {};
        let closestTask = null;
        let minDiff = Infinity;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (Array.isArray(data)) {
          data.forEach(schedule => {
            if (schedule.dueDate) {
              const dateStr = schedule.dueDate.split('T')[0];
              const dateObj = new Date(dateStr);
              const dayInt = dateObj.getDate();
              const taskMonth = dateObj.getMonth() + 1;
              const taskYear = dateObj.getFullYear();
              const currentMonth = new Date().getMonth() + 1;
              const currentYear = new Date().getFullYear();
              if (taskYear === currentYear && taskMonth === currentMonth) {
                if (!parsedTasks[dayInt]) parsedTasks[dayInt] = [];
                parsedTasks[dayInt].push(`${schedule.isDone ? '✅' : '🔥'} ${schedule.title}`);
              }
              if (!schedule.isDone) {
                const diff = Math.ceil((dateObj - now) / (1000 * 60 * 60 * 24));
                if (diff >= 0 && diff < minDiff) {
                  minDiff = diff;
                  closestTask = { dDay: diff === 0 ? 'D-Day' : `D-${diff}`, title: schedule.title, detail: schedule.type === 'EXAM' ? '시험 준비' : '마감 임박 과제' };
                }
              }
            }
          });
        }
        const aiDataRaw = localStorage.getItem('aiSavedSession');
        if (aiDataRaw) {
          try {
            const aiData = JSON.parse(aiDataRaw);
            const currentMonth = new Date().getMonth() + 1;
            const currentYear = new Date().getFullYear();
            Object.keys(aiData).forEach(dateKey => {
              const parts = dateKey.split('-').map(Number);
              if (parts.length === 3) {
                const [keyYear, keyMonth, keyDay] = parts;
                if (keyYear !== currentYear || keyMonth !== currentMonth) return;
                if (!parsedTasks[keyDay]) parsedTasks[keyDay] = [];
                aiData[dateKey].forEach(task => {
                  const alreadyExists = parsedTasks[keyDay].some(t => t.includes(task.title));
                  if (alreadyExists) return;
                  parsedTasks[keyDay].push(`🔥 ${task.title}`);
                  const taskDate = new Date(keyYear, keyMonth - 1, keyDay);
                  taskDate.setHours(0, 0, 0, 0);
                  const diff = Math.ceil((taskDate - now) / (1000 * 60 * 60 * 24));
                  if (diff >= 0 && diff < minDiff) {
                    minDiff = diff;
                    closestTask = { dDay: diff === 0 ? 'D-Day' : `D-${diff}`, title: task.title, detail: 'AI 추천 일정' };
                  }
                });
              }
            });
          } catch(e) {}
        }
        setTasksByDate(parsedTasks);
        if (closestTask) setUrgentTask(closestTask);
      }).catch(() => {});

    const loadTeams = (uid) => {
      const allTeamsLocal = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
      if (allTeamsLocal.length > 0) {
        setTeamProjects(allTeamsLocal.map(t => {
          let dDayStr = 'D-?';
          if (t.deadline) {
            const dateStr = t.deadline.split('T')[0];
            const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
            dDayStr = diff >= 0 ? (diff === 0 ? 'D-Day' : `D-${diff}`) : `D+${Math.abs(diff)}`;
          }
          return { id: t.id, name: t.projectTitle || t.name, progress: t.progress || 0, dDay: dDayStr, notice: t.noticeTitle || '공지사항이 없습니다.' };
        }));
      } else {
        setTeamProjects([]);
      }
    };
    loadTeams(userId);
    window.addEventListener('focus', () => loadTeams(userId));

    fetch(`${apiUrl}/api/resumes/${userId}`)
      .then(res => res.json())
      .then(data => {
        const serverList = Array.isArray(data) ? data : [];
        const localKey = `resumes_${userId}`;
        const localList = JSON.parse(localStorage.getItem(localKey) || '[]');
        const serverIds = new Set(serverList.map(r => String(r.id)));
        const localOnly = localList.filter(l => !serverIds.has(String(l.id)));
        const merged = [...serverList, ...localOnly];
        setResumes(merged.map(r => ({
          id: r.id,
          title: r.title || '(제목 없음)',
          date: r.updatedAt ? r.updatedAt.split('T')[0] : (r.createdAt ? r.createdAt.split('T')[0] : '최근'),
          keyword: r.company || r.category || '직무 미정',
          progress: r.analysis ? 100 : 60
        })));
      }).catch(() => {
        const localKey = `resumes_${userId}`;
        const localList = JSON.parse(localStorage.getItem(localKey) || '[]');
        setResumes(localList.map(r => ({
          id: r.id,
          title: r.title || '(제목 없음)',
          date: r.createdAt ? r.createdAt.split('T')[0] : '최근',
          keyword: r.company || r.category || '직무 미정',
          progress: r.analysis ? 100 : 60
        })));
      });

    fetch(`${apiUrl}/api/notices?university=${encodeURIComponent(universityName)}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setNotices(data.slice(0, 4).map((n, i) => ({ id: n.id || i, title: `[${n.category || '일반'}] ${n.title}`, content: n.title, isOpen: false })));
        }
      }).catch(() => {});

    fetch(`${aiUrl}/api/ai/jobs`)
      .then(res => res.json())
      .then(data => { if (data.jobs) setNewJobs(data.jobs); })
      .catch(() => {});

    fetch(`${apiUrl}/api/wish-companies/${userId}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const initial = data.map(c => ({
            id: c.id, name: c.companyName, dept: c.department || '직무 미정',
            url: c.url || '', analysisLoading: !!c.url,
            analysisSections: null, analysisFallback: null, isHiring: c.isHiring ?? true
          }));
          setSavedCompanies(initial);
          initial.forEach(company => {
            if (!company.url) {
              setSavedCompanies(prev => prev.map(c =>
                c.id === company.id ? { ...c, analysisLoading: false, analysisFallback: '채용 공고 URL이 없습니다.' } : c
              ));
              return;
            }
            fetch(`${aiUrl}/api/ai/analyze-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: company.url })
            })
              .then(r => r.json())
              .then(aiData => {
                const sections = parseAnalysisResult(aiData.analysis || '');
                setSavedCompanies(prev => prev.map(c =>
                  c.id === company.id
                    ? { ...c, analysisLoading: false, analysisSections: sections, analysisFallback: sections ? null : (aiData.analysis || '분석 결과가 없습니다.') }
                    : c
                ));
              })
              .catch(() => {
                setSavedCompanies(prev => prev.map(c =>
                  c.id === company.id ? { ...c, analysisLoading: false, analysisFallback: 'AI 분석 서버에 연결할 수 없습니다.' } : c
                ));
              });
          });
        }
      }).catch(() => {});

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', () => loadTeams(userId));
    };
  }, [apiUrl]);

  // ── 시간대 구분 ──────────────────────────────────────────────
  const getTimeOfDay = (date) => {
    const h = date.getHours();
    if (h >= 5  && h < 7)  return 'dawn';
    if (h >= 7  && h < 12) return 'morning';
    if (h >= 12 && h < 15) return 'noon';
    if (h >= 15 && h < 18) return 'afternoon';
    if (h >= 18 && h < 22) return 'evening';
    return 'night';
  };

  // ── 기본값 사진 (맑을 때) ────────────────────────────────────
  const TIME_CONFIG = {
    dawn:      { image: '/default_dawn.jpg',      message: '"좋은 아침이에요"' },
    morning:   { image: '/default_morning.jpg',   message: '"좋은 아침이에요"' },
    noon:      { image: '/default_noon.jpg',      message: '"오늘도 화이팅!"' },
    afternoon: { image: '/default_afternoon.jpg', message: '"오늘도 화이팅!"' },
    evening:   { image: '/default_evening.jpg',   message: '"하루 마무리하고 있네요"' },
    night:     { image: '/default_night.jpg',     message: '"늦은 시간까지 고생했어요"' },
  };

  // ── 날씨별 사진 ──────────────────────────────────────────────
  const WEATHER_CONFIG = {
    cloudy: {
      dawn:      '/cloudy_dawn.jpg',
      morning:   '/cloudy_dawn.jpg',
      noon:      '/cloudy_noon.jpg',
      afternoon: '/cloudy_noon.jpg',
      evening:   '/cloudy_evening.jpg',
      night:     '/cloudy_night.jpg',
    },
    rain: {
      dawn:      '/rain_dawn.jpg',
      morning:   '/rain_dawn.jpg',
      noon:      '/rain_noon.jpg',
      afternoon: '/rain_noon.jpg',
      evening:   '/rain_evening.jpg',
      night:     '/rain_night.jpg',
    },
    snow: {
      dawn:      '/snow_dawn.jpg',
      morning:   '/snow_dawn.jpg',
      noon:      '/snow_dawn.jpg',
      afternoon: '/snow_dawn.jpg',
      evening:   '/snow_evening.jpg',
      night:     '/snow_night.jpg',
    },
  };

  // ── 헤더 이미지/메시지 결정 (날씨 우선, 맑으면 시간대) ──────
  const currentTimeOfDay = getTimeOfDay(currentTime);
  const headerImage = weatherCondition
    ? WEATHER_CONFIG[weatherCondition][currentTimeOfDay]
    : TIME_CONFIG[currentTimeOfDay].image;
  const headerMessage = TIME_CONFIG[currentTimeOfDay].message;

  const formatTime = (date) =>
    date.toLocaleTimeString('ko-KR', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleNotice  = (id) => setNotices(notices.map(n => n.id === id ? { ...n, isOpen: !n.isOpen } : n));

  const handleAddCompany = async () => {
    if (!interestUrl || !newCompanyName) { alert('URL과 기업명을 모두 입력해주세요!'); return; }
    const userData = localStorage.getItem('currentUser');
    let userId = '2';
    if (userData) { try { userId = String(JSON.parse(userData).userId || '2'); } catch(e) {} }
    const tempId = Date.now();
    const dept = newCompanyDept || '직무 미정';
    setSavedCompanies(prev => [{ id: tempId, name: newCompanyName, dept, analysisLoading: true, analysisSections: null, analysisFallback: null, isHiring: true }, ...prev]);
    setInterestUrl(''); setNewCompanyName(''); setNewCompanyDept('');
    let realId = tempId;
    try {
      const res = await fetch(`${apiUrl}/api/wish-companies/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyName: newCompanyName, department: dept, url: interestUrl })
      });
      const saved = await res.json();
      if (saved.id) {
        realId = saved.id;
        setSavedCompanies(prev => prev.map(c => c.id === tempId ? { ...c, id: realId } : c));
      }
    } catch {}
    try {
      const aiRes = await fetch(`${aiUrl}/api/ai/analyze-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: interestUrl, companyName: newCompanyName })
      });
      const aiData = await aiRes.json();
      const analysisText = aiData.analysis || '';

      // 채용공고 관련 키워드 검증
      const jobKeywords = ['채용', '모집', '지원', '자격', '우대', '담당업무', '직무', '공고', '입사', '근무'];
      const hasJobContent = jobKeywords.some(kw => analysisText.includes(kw));

      if (!hasJobContent) {
        setSavedCompanies(prev => prev.map(c =>
          c.id === realId
            ? { ...c, analysisLoading: false, analysisSections: null, analysisFallback: '채용 공고 URL을 올려주세요!' }
            : c
        ));
      } else {
        const sections = parseAnalysisResult(analysisText);
        setSavedCompanies(prev => prev.map(c =>
          c.id === realId
            ? { ...c, analysisLoading: false, analysisSections: sections, analysisFallback: sections ? null : analysisText }
            : c
        ));
      }
    } catch {
      setSavedCompanies(prev => prev.map(c =>
        c.id === realId
          ? { ...c, analysisLoading: false, analysisSections: null, analysisFallback: 'AI 분석 서버에 연결할 수 없습니다.' }
          : c
      ));
    }
  };

  const handleAnalyzeUrl = async () => {
    if (!urlInput) { alert('채용 공고 URL을 입력해주세요.'); return; }

    // URL 형식 기본 검증
    try { new URL(urlInput); } catch { alert('올바른 URL 형식이 아닙니다.'); return; }

    setIsAnalyzing(true);
    setUrlAnalysisResult('');
    try {
      const res = await fetch(`${aiUrl}/api/ai/analyze-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlInput })
      });
      const data = await res.json();
      const analysis = data.analysis || '';

      // 채용공고 관련 키워드 검증
      const jobKeywords = ['채용', '모집', '지원', '자격', '우대', '담당업무', '직무', '공고', '입사', '근무'];
      const hasJobContent = jobKeywords.some(kw => analysis.includes(kw));

      if (!hasJobContent) {
        setUrlAnalysisResult('채용 공고 URL을 올려주세요!');
      } else {
        setUrlAnalysisResult(analysis || '분석 실패! 올바른 URL인지 확인해주세요.');
      }
    } catch {
      setUrlAnalysisResult('서버 연결 실패!');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const SECTION_CONFIG = [
    { key: '기업 인재상', emoji: '🏢', color: '#3a8fd4', bg: '#f0f7ff', border: '#bfdbfe' },
    { key: '필요 역량',   emoji: '💡', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
    { key: '자소서 전략', emoji: '✍️', color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
  ];

  const parseAnalysisResult = (text) => {
    if (!text || text === '분석 중...' || text === '서버 연결 실패!') return null;
    const sections = [];
    SECTION_CONFIG.forEach(({ key, emoji, color, bg, border }) => {
      const pattern = new RegExp(
        `(?:\\[${key}\\]|\\*\\*${key}\\*\\*|##?\\s*${key})[:\\s]*([\\s\\S]*?)(?=\\[|\\*\\*[가-힣]+\\*\\*|##|$)`,
        'i'
      );
      const match = text.match(pattern);
      if (match && match[1]) {
        const raw = match[1].trim();
        const items = raw
          .split(/\n|\d+\.\s+/)
          .map(s => s.replace(/\*\*/g, '').trim())
          .filter(s => s.length > 0);
        sections.push({ key, emoji, color, bg, border, items });
      }
    });
    if (sections.length === 0) return null;
    return sections;
  };

  const renderAnalysisResult = () => {
    if (isAnalyzing) {
      return (
        <div style={S.analyzingBox}>
          <div style={S.spinner} />
          <span style={{ fontSize: '13px', color: '#3a8fd4', fontWeight: '500' }}>AI가 채용 공고를 분석하고 있습니다...</span>
        </div>
      );
    }
    if (!urlAnalysisResult) {
      return (
        <div style={S.resultBox}>
          <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', lineHeight: '1.7' }}>
            채용 공고 URL을 입력하시면 AI 분석 결과가 표시됩니다.
          </p>
        </div>
      );
    }
    const sections = parseAnalysisResult(urlAnalysisResult);
    if (!sections) {
      return (
        <div style={{ ...S.resultBox, borderLeft: '3px solid #3a8fd4', backgroundColor: '#f0f7ff', borderRadius: '0 8px 8px 0' }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', lineHeight: '1.7' }}>
            <strong style={{ color: '#3a8fd4' }}>AI 분석 결과:</strong> {urlAnalysisResult}
          </p>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {sections.map(({ key, emoji, color, bg, border, items }) => (
          <div key={key} style={{ backgroundColor: bg, border: `1px solid ${border}`, borderRadius: '10px', padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px' }}>{emoji}</span>
              <span style={{ fontSize: '12px', fontWeight: '700', color, letterSpacing: '0.3px' }}>{key}</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: '0', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {items.map((item, idx) => (
                <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#374151', lineHeight: '1.6' }}>
                  <span style={{ color, fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const renderCalendar = () => {
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const now = new Date();
    const calYear = now.getFullYear();
    const calMonth = now.getMonth() + 1;
    const daysInMonth = new Date(calYear, now.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(calYear, now.getMonth(), 1).getDay();
    const dates = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const emptyCells = Array.from({ length: firstDayOfMonth }, () => null);
    const todayInt = now.getDate();
    return (
      <div style={S.calWrapper}>
        <div style={S.calHeader}>{calYear}년 {calMonth}월</div>
        <div style={S.calGrid}>
          {days.map((day, i) => (
            <div key={day} style={{ ...S.calDayLabel, color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#6b7280' }}>{day}</div>
          ))}
          {emptyCells.map((_, i) => <div key={`e-${i}`} />)}
          {dates.map(date => {
            const isToday    = date === todayInt;
            const isSelected = selectedDate === date;
            const hasTask    = tasksByDate[date]?.length > 0;
            return (
              <div key={date} style={S.calCell} onClick={() => setSelectedDate(date)}>
                <span style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: isToday || isSelected ? '700' : '400',
                  backgroundColor: isToday ? BLUE : isSelected ? '#1e40af' : 'transparent',
                  color: isToday || isSelected ? '#fff' : '#374151',
                }}>
                  {date}
                </span>
                {hasTask && <div style={S.calDot} />}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const Ico = {
    Book:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
    Cal:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    Team:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    Bell:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    File:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    Link:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
    Star:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    Job:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    Menu:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    Logout: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  };

  const CardHeader = ({ icon, title }) => (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );

  return (
    <div style={S.root}>

      {/* ── 로그아웃 모달 ── */}
      {showLogoutModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '24px' }}>로그아웃하시겠습니까?</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button style={S.modalOk} onClick={() => { localStorage.removeItem('currentUser'); localStorage.removeItem('token'); navigate('/'); }}>확인</button>
              <button style={S.modalCancel} onClick={() => setShowLogoutModal(false)}>취소</button>
            </div>
          </div>
        </div>
      )}

      {isSidebarOpen && <div style={S.overlay} onClick={toggleSidebar} />}

      {/* ── 사이드바 (모바일: 75vw) ── */}
      <div style={{
        ...S.sidebar,
        width: isMobile ? '75vw' : '260px',
        transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', letterSpacing: '2px' }}>MENU</span>
          <button style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '22px', cursor: 'pointer' }} onClick={toggleSidebar}>✕</button>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            { icon: Ico.Book, label: 'AI 강의 학습',    path: '/upload' },
            { icon: Ico.Cal,  label: 'AI 플래너',        path: '/planner' },
            { icon: Ico.Team, label: '팀플 관리',         path: '/team-project' },
            { icon: Ico.Bell, label: '공지사항',          path: '/notice-board' },
            { icon: Ico.File, label: 'AI 자소서 매니저', path: '/cover-letter' },
          ].map(item => (
            <div
              key={item.path}
              style={S.navItem}
              onClick={() => { navigate(item.path); toggleSidebar(); }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#e6f1fb', color: '#3a8fd4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {item.icon}
              </div>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>{item.label}</span>
            </div>
          ))}
        </nav>
      </div>

      {/* ── 메인 콘텐츠 ── */}
      <main style={{
        ...S.main,
        padding: isMobile ? '16px 14px 0' : '48px 32px 0',
        gap: isMobile ? '16px' : '32px',
      }}>

        {/* ════ 헤더 ════ */}
        <header style={{
          ...S.topHeader,
          flexDirection: isMobile ? 'column' : 'row',
          padding: isMobile ? '56px 20px 20px' : '44px 80px 44px 60px',
          gap: isMobile ? '0' : '0',
          alignItems: isMobile ? 'center' : 'center',
          backgroundImage: `url(${headerImage})`,
          backgroundSize: 'cover',
          animation: 'none',
          backgroundPosition: 'center top',
        }}>
          {/* ── 모바일 전용: 왼쪽 상단 버튼 (absolute, 크기 축소) ── */}
          {isMobile && (
            <div style={{
              position: 'absolute', top: '14px', left: '14px',
              display: 'flex', flexDirection: 'row', gap: '6px', zIndex: 2,
            }}>
              <button style={{ ...S.menuOpenBtn, padding: '6px 10px', fontSize: '11px' }} onClick={toggleSidebar}>
                {Ico.Menu}&nbsp;&nbsp;MENU
              </button>
              <button style={{ ...S.logoutBtn, padding: '6px 10px', fontSize: '11px' }} onClick={() => setShowLogoutModal(true)}>
                {Ico.Logout}&nbsp;로그아웃
              </button>
            </div>
          )}

          {/* 데스크탑 전용: 왼쪽 버튼 블록 */}
          {!isMobile && (
            <div style={{ ...S.leftBlock, marginLeft: '-20px' }}>
              <button style={S.menuOpenBtn} onClick={toggleSidebar}>
                {Ico.Menu}&nbsp;&nbsp;MENU
              </button>
              <button style={S.logoutBtn} onClick={() => setShowLogoutModal(true)}>
                {Ico.Logout}&nbsp;로그아웃
              </button>
            </div>
          )}

          {/* 인사말 — 모바일/데스크탑 공통, 항상 중앙 */}
          <div style={{
            ...S.centerBlock,
            flex: isMobile ? 'none' : 1,
            marginTop: isMobile ? '10px' : '0',
          }}>
            <h1 style={{ ...S.greetingText, marginBottom: isMobile ? '2px' : '4px' }}>
              안녕하세요, <span style={{ fontWeight: '700', fontSize: '18px', color: '#fff' }}>{userName}</span>님!
            </h1>
            <p style={{ fontSize: '22px', fontWeight: '600', color: '#fff', margin: '0 0 6px', textShadow: '0 1px 6px rgba(0,0,0,0.25)' }}>
              {headerMessage}
            </p>
            <p style={{ ...S.universityText, margin: 0 }}>
              {university} | 자동 로그아웃{' '}
              {(() => {
                const last = parseInt(localStorage.getItem('loginTime') || Date.now());
                const remaining = Math.max(0, 60 * 60 * 1000 - (currentTime.getTime() - last));
                const mm = String(Math.floor(remaining / 60000)).padStart(2, '0');
                const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
                return `${mm}:${ss}`;
              })()}
            </p>
          </div>

          {/* 날짜/시간 (데스크탑) */}
          {!isMobile && (
            <div style={{ ...S.timeBlock, marginRight: '-20px' }}>
              <div style={S.dateDisplay}>
                {currentTime.getMonth() + 1}월 {currentTime.getDate()}일 ({['일','월','화','수','목','금','토'][currentTime.getDay()]})
              </div>
              <div style={S.timeDisplay}>{formatTime(currentTime)}</div>
            </div>
          )}

          {/* 모바일 전용: 날짜/시간 인사말 바로 아래, 오른쪽 정렬 */}
          {isMobile && (
            <div style={{ textAlign: 'right', width: '100%', marginTop: '6px' }}>
              <div style={{ ...S.dateDisplay, fontSize: '11px', marginBottom: '1px' }}>
                {currentTime.getMonth() + 1}월 {currentTime.getDate()}일 ({['일','월','화','수','목','금','토'][currentTime.getDay()]})
              </div>
              <div style={{ ...S.timeDisplay, fontSize: '11px' }}>{formatTime(currentTime)}</div>
            </div>
          )}
        </header>

        <div style={S.divider} />

        {/* ════ ROW 1: AI 강의 학습 | 공지사항 (모바일: 1열) ════ */}
        <div style={{ ...S.row2, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '20px' }}>
          <div style={S.card}>
            <CardHeader icon={Ico.Book} title="AI 강의 학습 요약" />
            <div style={S.infoBox}>
              <p style={S.boxLabel}>최근 요약</p>
              <p style={S.boxTitle}>{aiSummary.title}</p>
              <p style={S.boxBody}>{aiSummary.content}</p>
            </div>
            <div style={S.quizBox}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <p style={{ ...S.boxLabel, color: '#3a8fd4', margin: 0 }}>오늘의 예상문제</p>
                <button style={S.quizBtn} onClick={() => setShowAnswer(!showAnswer)}>
                  {showAnswer ? '숨기기' : '정답 보기'}
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{dailyQuiz.question}</p>
              {showAnswer && (
                <div style={{ marginTop: '10px', padding: '10px 12px', backgroundColor: '#dbeafe', borderRadius: '6px', fontSize: '13px', color: '#1e40af' }}>
                  <strong>[정답]</strong> {dailyQuiz.answer}
                </div>
              )}
            </div>
          </div>

          <div style={S.card}>
            <CardHeader icon={Ico.Bell} title="공지사항" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {notices.length > 0 ? notices.map(notice => (
                <div key={notice.id} style={S.noticeRow} onClick={() => toggleNotice(notice.id)}>
                  <span style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', flex: 1 }}>{notice.title}</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px', flexShrink: 0 }}>{notice.isOpen ? '▲' : '▼'}</span>
                  {notice.isOpen && (
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.5', paddingLeft: '8px', borderLeft: '2px solid #3a8fd4', width: '100%' }}>
                      {notice.content}
                    </p>
                  )}
                </div>
              )) : (
                <p style={S.emptyText}>불러오는 중...</p>
              )}
            </div>
          </div>
        </div>

        {/* ════ ROW 2: 채용공고 (모바일: 2열) ════ */}
        <div style={S.card}>
          <CardHeader icon={Ico.Job} title="오늘 뜬 채용 공고" />
          <div style={{
            ...S.jobGrid,
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
            gap: isMobile ? '10px' : '14px',
          }}>
            {newJobs.length > 0 ? newJobs.map((job, idx) => (
              <div key={idx} style={S.jobCard}>
                <strong style={{ fontSize: '13px', color: '#1e293b', display: 'block', marginBottom: '4px' }}>{job.companyName}</strong>
                <span style={{ ...S.badgeRed, display: 'inline-block', marginBottom: '6px', fontSize: '9px', padding: '1px 5px' }}>{job.deadline || '채용시 마감'}</span>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{job.title}</p>
              </div>
            )) : (
              <p style={S.emptyText}>최신 채용 공고를 불러오는 중입니다.</p>
            )}
          </div>
        </div>

        {/* ════ ROW 3: AI 플래너 | 팀플 관리 (모바일: 1열) ════ */}
        <div style={{ ...S.row2, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '20px' }}>

          {/* AI 플래너 */}
          <div style={{ ...S.card, minHeight: isMobile ? 'auto' : '480px', display: 'flex', flexDirection: 'column' }}>
            <CardHeader icon={Ico.Cal} title="AI 플래너" />
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              margin: isMobile ? '12px 0 0' : '12px 56px 0',
            }}>
              <div>{renderCalendar()}</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '10px',
              }}>
                <div style={S.taskBox}>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                    📅 {new Date().getMonth() + 1}월 {selectedDate}일 일정
                  </p>
                  {tasksByDate[selectedDate]?.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '11px', color: '#374151', lineHeight: '1.6' }}>
                      {tasksByDate[selectedDate].map((task, idx) => <li key={idx}>{task}</li>)}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>등록된 일정이 없습니다.</p>
                  )}
                </div>
                <div style={S.dDayRow}>
                  <span style={S.dDayBadge}>{urgentTask.dDay}</span>
                  <div>
                    <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{urgentTask.title}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{urgentTask.detail}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 팀플 관리 */}
          <div style={{ ...S.card, minHeight: isMobile ? 'auto' : '480px', display: 'flex', flexDirection: 'column' }}>
            <CardHeader icon={Ico.Team} title="팀플 관리" />
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
              margin: isMobile ? '12px 0 0' : '24px 56px 0',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {teamProjects.length > 0 ? teamProjects.slice(0, 2).map((team, idx) => (
                  <div
                    key={team.id}
                    style={{
                      paddingBottom: idx === 0 && teamProjects.length > 1 ? '16px' : '0',
                      borderBottom: idx === 0 && teamProjects.length > 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '9px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{team.name}</span>
                      <span style={S.badgeRed}>{team.dDay}</span>
                    </div>
                    <div style={S.gaugeTrack}>
                      <div style={{ ...S.gaugeFill, width: `${team.progress}%`, backgroundColor: idx === 0 ? '#3a8fd4' : '#60a5fa' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>진행률</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: idx === 0 ? '#3a8fd4' : '#60a5fa' }}>{team.progress}%</span>
                    </div>
                    {team.notice && team.notice !== '공지사항이 없습니다.' && (
                      <p style={S.teamNotice}>📢 {team.notice}</p>
                    )}
                  </div>
                )) : (
                  <p style={S.emptyText}>진행 중인 팀 프로젝트가 없습니다.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ════ ROW 4: URL 기업 분석 + 자소서 | 관심 기업 (모바일: 1열) ════ */}
        <div style={{ ...S.row2, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '20px', marginBottom: '60px' }}>

          <div style={S.card}>
            <CardHeader icon={Ico.Link} title="URL 기업 분석" />
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <input
                style={S.input}
                placeholder="분석할 채용 공고 URL 입력"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAnalyzeUrl()}
              />
              <button style={S.primaryBtn} onClick={handleAnalyzeUrl} disabled={isAnalyzing}>
                {isAnalyzing ? '분석 중...' : '분석하기'}
              </button>
            </div>
            {renderAnalysisResult()}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '16px 0 10px' }}>
              <div style={S.cardIcon}>{Ico.File}</div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>내 자소서 현황</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {resumes.length > 0 ? resumes.slice(0, 2).map(r => (
                <div key={r.id} style={S.resumeRow}>
                  <div>
                    <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{r.title}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>수정: {r.date} · {r.keyword}</p>
                  </div>
                  <span style={{
                    ...S.badge,
                    ...(r.progress === 100 ? { backgroundColor: '#dcfce7', color: '#15803d' } : { backgroundColor: '#fef9c3', color: '#854d0e' }),
                    flexShrink: 0,
                  }}>
                    {r.progress === 100 ? '작성 완료' : '작성 중'}
                  </span>
                </div>
              )) : (
                <p style={S.emptyText}>작성 중인 자소서가 없습니다.</p>
              )}
            </div>
          </div>

          <div style={S.card}>
            <CardHeader icon={Ico.Star} title="관심 기업" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
              <input style={S.input} placeholder="채용 공고 URL (필수)" value={interestUrl} onChange={e => setInterestUrl(e.target.value)} />
              <div style={{ display: 'flex', gap: '6px' }}>
                <input style={{ ...S.input, flex: 1 }} placeholder="기업명 (최대 20자)" value={newCompanyName} onChange={e => { if (e.target.value.length <= 20) setNewCompanyName(e.target.value); }} maxLength={20} />
                <input style={{ ...S.input, flex: 1 }} placeholder="직무 (최대 20자)" value={newCompanyDept} onChange={e => { if (e.target.value.length <= 20) setNewCompanyDept(e.target.value); }} maxLength={20} />
                <button style={S.primaryBtn} onClick={handleAddCompany}>추가</button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto' }}>
              {savedCompanies.length > 0 ? savedCompanies.map(company => (
                <div key={company.id} style={S.companyCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{company.name}</span>
                      <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '5px' }}>({company.dept})</span>
                    </div>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      {company.isHiring && (
                        <span style={{ ...S.badge, backgroundColor: '#dcfce7', color: '#15803d' }}>채용중</span>
                      )}
                      <button
                        style={S.deleteBtn}
                        onClick={async () => {
                          const userData = localStorage.getItem('currentUser');
                          let userId = '2';
                          if (userData) { try { userId = String(JSON.parse(userData).userId || '2'); } catch(e) {} }

                          console.log('삭제 요청 - userId:', userId, 'companyId:', company.id); // ✅ 디버깅용

                          try {
                            const res = await fetch(`${apiUrl}/api/wish-companies/${userId}/${company.id}`, { method: 'DELETE' });
                            console.log('삭제 응답 상태:', res.status); // ✅ 디버깅용
                            if (res.ok) {
                              setSavedCompanies(prev => prev.filter(c => c.id !== company.id));
                            } else {
                              alert('삭제에 실패했습니다. 다시 시도해주세요.');
                            }
                          } catch(e) {
                            console.error('삭제 오류:', e);
                            alert('서버 연결 실패!');
                          }
                        }}
                      >삭제</button>
                    </div>
                  </div>
                  {company.analysisLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                      <div style={S.spinner} />
                      <span style={{ fontSize: '11px', color: '#3a8fd4' }}>AI 분석 중...</span>
                    </div>
                  ) : company.analysisSections ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {company.analysisSections.map(({ key, emoji, color, bg, border, items }) => (
                        <div key={key} style={{ backgroundColor: bg, border: `1px solid ${border}`, borderRadius: '8px', padding: '8px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '5px' }}>
                            <span style={{ fontSize: '12px' }}>{emoji}</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color }}>{key}</span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {items.map((item, idx) => (
                              <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '5px', fontSize: '11px', color: '#374151', lineHeight: '1.55' }}>
                                <span style={{ color, fontWeight: '700', flexShrink: 0 }}>•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', lineHeight: '1.6' }}>
                      {company.analysisFallback || 'AI 분석 결과가 없습니다.'}
                    </p>
                  )}
                </div>
              )) : (
                <p style={S.emptyText}>등록된 관심 기업이 없습니다.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ── 스타일 토큰 (원본 그대로) ──────────────────────────────
const BLUE = '#3a8fd4';

const S = {
  root:           { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif", justifyContent: 'center', overflowX: 'hidden', width: '100%' },
  sidebar:        { position: 'fixed', left: 0, top: 0, height: '100vh', width: '260px', backgroundColor: '#fff', borderRight: '1px solid #e2e8f0', transition: '0.25s', zIndex: 1000, padding: '32px 20px', boxShadow: '4px 0 20px rgba(0,0,0,0.06)' },
  navItem:        { display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s' },
  overlay:        { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.25)', zIndex: 999 },
  main:           { width: '100%', maxWidth: '1600px', padding: '48px 32px 0', display: 'flex', flexDirection: 'column', gap: '32px', boxSizing: 'border-box', overflowX: 'hidden', minWidth: 0 },
  topHeader:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#3a8fd4', padding: '44px 80px 44px 60px', borderRadius: '20px', color: '#fff', position: 'relative', overflow: 'hidden' },
  leftBlock:      { display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 1, marginLeft: '-20px' },
  menuOpenBtn:    { display: 'flex', alignItems: 'center', padding: '9px 16px', fontSize: '13px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.22)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '10px', cursor: 'pointer', letterSpacing: '0.5px' },
  logoutBtn:      { display: 'flex', alignItems: 'center', padding: '9px 16px', fontSize: '13px', fontWeight: '500', backgroundColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', cursor: 'pointer', width: 'fit-content' },
  centerBlock:    { textAlign: 'center', flex: 1, zIndex: 1 },
  greetingText:   { fontSize: '16px', fontWeight: '400', letterSpacing: '-0.3px', margin: '0 0 6px 0', color: 'rgba(255,255,255,0.9)' },
  universityText: { fontSize: '14px', fontWeight: '400', color: 'rgba(255,255,255,0.65)', margin: 0 },
  timeBlock:      { textAlign: 'right', marginRight: '-20px', zIndex: 1 },
  dateDisplay:    { fontSize: '16px', fontWeight: '300', letterSpacing: '0.3px', marginBottom: '4px' },
  timeDisplay:    { fontSize: '16px', fontWeight: '400', color: 'rgba(255,255,255,0.78)' },
  divider:        { height: '1px', backgroundColor: '#e2e8f0', margin: '8px 0' },
  row2:           { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  card:           { backgroundColor: '#fff', borderRadius: '14px', padding: '22px', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9', boxSizing: 'border-box', minWidth: 0, width: '100%' },
  cardHeader:     { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  cardIcon:       { width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e6f1fb', color: BLUE },
  cardTitle:      { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  infoBox:        { padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '10px' },
  boxLabel:       { margin: '0 0 3px', fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },
  boxTitle:       { margin: '0 0 3px', fontSize: '13px', fontWeight: '600', color: '#1e293b' },
  boxBody:        { margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.5' },
  quizBox:        { padding: '12px 14px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' },
  quizBtn:        { padding: '4px 10px', fontSize: '11px', fontWeight: '600', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' },
  noticeRow:      { padding: '11px 13px', backgroundColor: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '8px', cursor: 'pointer', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' },
  jobGrid:        { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px' },
  jobCard:        { padding: '16px 18px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer' },
  badgeRed:       { display: 'inline-block', padding: '2px 7px', borderRadius: '5px', fontSize: '10px', fontWeight: '600', backgroundColor: '#fee2e2', color: '#b91c1c' },
  calWrapper:     { marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  calHeader:      { fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '10px', marginTop: '-14px', textAlign: 'center' },
  calGrid:        { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', textAlign: 'center', marginBottom: '10px', width: '100%' },
  calDayLabel:    { fontSize: '12px', fontWeight: '500', padding: '2px 0 5px', color: '#94a3b8' },
  calCell:        { height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', transition: 'background 0.15px' },
  calDot:         { width: '3px', height: '3px', backgroundColor: '#ef4444', borderRadius: '50%', position: 'absolute', bottom: '1px', left: '50%', transform: 'translateX(-50%)' },
  taskBox:        { padding: '8px 12px', backgroundColor: '#f0f7ff', borderLeft: '3px solid ' + BLUE, borderRadius: '0 8px 8px 0', marginBottom: '8px', marginTop: '8px' },
  dDayRow:        { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 14px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', marginTop: '4px' },
  dDayBadge:      { fontSize: '18px', fontWeight: '800', color: '#ea580c', flexShrink: 0 },
  gaugeTrack:     { width: '100%', height: '5px', backgroundColor: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' },
  gaugeFill:      { height: '100%', borderRadius: '99px', transition: 'width 0.5s' },
  teamNotice:     { margin: '8px 0 0', padding: '8px 11px', backgroundColor: '#fef9c3', borderLeft: '2px solid #fbbf24', borderRadius: '0 6px 6px 0', fontSize: '11px', color: '#92400e' },
  input:          { flex: 1, padding: '9px 12px', borderRadius: '7px', border: '1px solid #e2e8f0', fontSize: '12px', outline: 'none', color: '#374151', backgroundColor: '#f8fafc', minWidth: 0, boxSizing: 'border-box' },
  primaryBtn:     { padding: '9px 13px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' },
  resultBox:      { padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '52px' },
  analyzingBox:   { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #bfdbfe' },
  spinner:        { width: '16px', height: '16px', flexShrink: 0, border: '2px solid #bfdbfe', borderTop: '2px solid #3a8fd4', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  resumeRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' },
  badge:          { display: 'inline-block', padding: '2px 7px', borderRadius: '5px', fontSize: '10px', fontWeight: '600' },
  companyCard:    { padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' },
  deleteBtn:      { padding: '2px 6px', fontSize: '10px', fontWeight: '600', border: '1px solid #fca5a5', color: '#ef4444', backgroundColor: 'transparent', borderRadius: '4px', cursor: 'pointer' },
  emptyText:      { textAlign: 'center', color: '#94a3b8', fontSize: '12px', padding: '16px 0', margin: 0 },
  modalOverlay:   { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
  modal:          { backgroundColor: '#fff', padding: '32px 40px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', textAlign: 'center', minWidth: '280px' },
  modalOk:        { padding: '9px 24px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  modalCancel:    { padding: '9px 24px', backgroundColor: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

if (typeof document !== 'undefined' && !document.getElementById('spin-style')) {
  const style = document.createElement('style');
  style.id = 'spin-style';
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes cloudMove { 0% { background-position: center top; } 100% { background-position: 20% top; } }
    @keyframes rainFall { 0% { background-position: center 0%; } 100% { background-position: center 3%; } }
    @keyframes snowFall { 0% { background-position: center 0%; } 100% { background-position: center 2%; } }
  `;
  document.head.appendChild(style);
}

export default MainPage;